<template>
    <div id="app">
        <div class="d-flex">
            <AppSidebar v-if="showSecure" />
            <div id="main-app-content" :class="{ 'main-full-width': !showSecure }">
                <router-view name="header"></router-view>
                <main id="app-main">
                    <router-view v-slot="{ Component }">
                        <transition name="page-fade" mode="out-in">
                            <component :is="Component" />
                        </transition>
                    </router-view>
                </main>
                <notifications
                    :group="toastGroup"
                    position="bottom right"
                    :width="380"
                    :max="4"
                    :speed="350"
                    :duration="5000"
                    ignore-duplicates
                    pause-on-hover
                    close-on-click
                    animation-name="app-toast"
                >
                    <template #body="{ item, close }">
                        <AppToast :item="item" :close="close" />
                    </template>
                </notifications>
                <router-view name="footer"></router-view>
            </div>
        </div>
    </div>
</template>
<script lang="ts">
import { defineComponent, computed } from 'vue';
import store from '@/store';
import AppSidebar from '@/components/layout/AppSidebar.vue';
import AppToast from '@/components/layout/AppToast.vue';
import { TOAST_GROUP } from '@/composables/useToast';

export default defineComponent({
    components: {
        AppSidebar,
        AppToast
    },
    setup() {
        const showSecure = computed((): Boolean => {
            return store.state.showSecure
        })

        return {
            showSecure,
            toastGroup: TOAST_GROUP
        }
    }
})
</script>

<style lang="scss">
#app {
    font-family: Avenir, Helvetica, Arial, sans-serif;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
    color: #2c3e50;
}

#nav {
    padding: 30px;

    a {
        font-weight: bold;
        color: #2c3e50;

        &.router-link-exact-active {
        color: #42b983;
        }
    }
}
#app-main {
    min-height: 85vh;
    background: #fdfdfd;
}
#main-side-nav {
    width: 5%;
    height: 100vh;
    background: #03363D;
}
#main-app-content {
    width: 95%
}
.main-full-width {
    width: 100% !important;
}
.logo-container {
    padding: 20px 12px 24px 12px;
    display: flex;
    justify-content: center;
    align-items: center;
}

.logo-link {
    text-decoration: none;
    display: block;
    transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);

    &:hover {
        transform: scale(1.05);
    }

    &:active {
        transform: scale(0.98);
    }
}

.logo-wrapper {
    background: rgba(255, 255, 255, 0.08);
    padding: 12px 16px;
    border-radius: 10px;
    display: flex;
    justify-content: center;
    align-items: center;
    transition: all 0.3s ease;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);

    &:hover {
        background: rgba(255, 255, 255, 0.12);
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
    }
}

.main-side-nav-img {
    width: auto;
    height: 46px;
    display: block;
    filter: brightness(1);
    transition: filter 0.3s ease;
}

.logo-link:hover .main-side-nav-img {
    filter: brightness(1.1);
}

.main-side-nav-icon {
    color: white;
    font-size: 24px;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.nav-section-header {
    text-align: center;
    padding: 16px 8px 8px 8px;
    margin: 12px 8px 0 8px;
    border-bottom: 2px solid rgba(255, 255, 255, 0.2);

    span {
        color: rgba(255, 255, 255, 0.8);
        font-size: 11px;
        font-weight: 700;
        text-transform: uppercase;
        letter-spacing: 1px;
    }
}

.row.my-4 {
    transition: all 0.3s ease;

    &:hover {
        background: rgba(255, 255, 255, 0.05);
    }

    a {
        padding: 8px;
        border-radius: 8px;
        display: flex;
        align-items: center;
        justify-content: center;
        transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);

        &:hover .main-side-nav-icon {
            transform: scale(1.15);
            color: #FFFFFF;
            filter: drop-shadow(0 2px 4px rgba(255, 255, 255, 0.3));
        }

        &.router-link-active .main-side-nav-icon {
            color: #FFFFFF;
            filter: drop-shadow(0 2px 6px rgba(255, 255, 255, 0.4));
            transform: scale(1.1);
        }
    }
}

/* Toast outlet: sit above every modal/overlay in the app and keep clear of the edges */
.vue-notification-group {
    z-index: 11000;
    margin: 0 20px 20px 0;
    max-width: calc(100vw - 40px);
}
.vue-notification-wrapper {
    overflow: visible;
}

/* Toast motion: settle in from the right, drift out and shrink slightly, and let
   the rest of the stack glide into the gap.

   The outlet writes `transition: all <speed>ms` inline on every wrapper, which
   would silently override these curves, hence the !important. */
.app-toast-enter-active {
    transition:
        opacity 0.3s ease-out,
        transform 0.4s cubic-bezier(0.22, 1, 0.36, 1) !important;
}
.app-toast-move {
    transition: transform 0.4s cubic-bezier(0.22, 1, 0.36, 1) !important;
}
/* Declared after -move so a toast that is both moving and leaving keeps the leave curve
   (and its opacity fade) rather than the move rule's transform-only transition. */
.app-toast-leave-active {
    /* Out of flow so siblings reflow (and animate via -move) as soon as the leave starts,
       instead of jumping when the element is finally removed. */
    position: absolute;
    left: 0;
    right: 0;
    pointer-events: none;
    transition:
        opacity 0.3s cubic-bezier(0.4, 0, 1, 1),
        transform 0.4s cubic-bezier(0.4, 0, 0.2, 1) !important;
}
.app-toast-enter-from {
    opacity: 0;
    transform: translateX(24px) scale(0.98);
}
.app-toast-leave-to {
    opacity: 0;
    transform: translateX(24px) scale(0.96);
}
@media (prefers-reduced-motion: reduce) {
    .app-toast-enter-active,
    .app-toast-leave-active,
    .app-toast-move {
        transition: opacity 0.15s ease !important;
    }
    .app-toast-enter-from,
    .app-toast-leave-to {
        transform: none;
    }
}

/* Page Transition Animations */
.page-fade-enter-active {
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.page-fade-leave-active {
    transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
}

.page-fade-enter-from {
    opacity: 0;
    transform: translateY(10px);
}

.page-fade-leave-to {
    opacity: 0;
    transform: translateY(-5px);
}

</style>
